import React, { useState, useEffect } from "react"
import {
	AppBar,
	Avatar,
	Box,
	Button,
	CircularProgress,
	Container,
	Divider,
	Drawer,
	FormControl,
	IconButton,
	InputLabel,
	List,
	ListItemButton,
	ListItemIcon,
	ListItemText,
	Menu,
	MenuItem,
	Popover,
	Select,
	Toolbar,
	Tooltip,
	Typography,
} from "@mui/material"
import { alpha, useTheme } from "@mui/material/styles"
import MenuRoundedIcon from "@mui/icons-material/MenuRounded"
import NotificationsNoneRoundedIcon from "@mui/icons-material/NotificationsNoneRounded"
import CalculateRoundedIcon from "@mui/icons-material/CalculateRounded"
import AccountCircleRoundedIcon from "@mui/icons-material/AccountCircleRounded"
import DarkModeRoundedIcon from "@mui/icons-material/DarkModeRounded"
import LightModeRoundedIcon from "@mui/icons-material/LightModeRounded"
import PersonIcon from "@mui/icons-material/Person"
import CreditCardIcon from "@mui/icons-material/CreditCard"
import LocalOfferRoundedIcon from "@mui/icons-material/LocalOfferRounded"
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings"
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded"
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar"
import LocalShippingIcon from "@mui/icons-material/LocalShipping"
import DirectionsBusIcon from "@mui/icons-material/DirectionsBus"
import TwoWheelerIcon from "@mui/icons-material/TwoWheeler"
import AgricultureIcon from "@mui/icons-material/Agriculture"
import { useLocation, useNavigate } from "react-router-dom"
import { useAuthUser, useLogout } from "../hooks/useAuthUser"
import { useColorMode } from "../theme/colorMode"
import { TariffsApi } from "../services/api"

// Типы и константы для калькулятора
interface Tariff {
	id: string
	vehicleType: string
	days15: number
	month1: number
	month2: number
	month3: number
	month4: number
	month5: number
	month6: number
	month7: number
	month8: number
	month9: number
	month10: number
	month11: number
	month12: number
}

const insurancePeriods = [
	{ value: "15days", label: "15 дней" },
	{ value: "1", label: "1 месяц" },
	{ value: "2", label: "2 месяца" },
	{ value: "3", label: "3 месяца" },
	{ value: "4", label: "4 месяца" },
	{ value: "5", label: "5 месяцев" },
	{ value: "6", label: "6 месяцев" },
	{ value: "7", label: "7 месяцев" },
	{ value: "8", label: "8 месяцев" },
	{ value: "9", label: "9 месяцев" },
	{ value: "10", label: "10 месяцев" },
	{ value: "11", label: "11 месяцев" },
	{ value: "12", label: "12 месяцев" },
]

const vehicleTypeTranslations: Record<string, string> = {
	"Legkovye avtomobili": "Легковые автомобили",
	"Pricepy k legkovym avtomobilyam": "Прицепы к легковым авто",
	"Gruzovye avtomobili, tyagachi": "Грузовые автомобили",
	"Pricepy i polupricepy k gruzovym avtomobilyam i tyagacham": "Прицепы к грузовым авто",
	"Avtobusy": "Автобусы",
	"Motocikly, motorrollery, motokoljaska i mopedy": "Мотоциклы, мопеды",
	"Selskokhozyajstvennaya i stroitelnaya tekhnika": "Спецтехника",
}

const vehicleIcons: Record<string, React.ReactNode> = {
	"Legkovye avtomobili": <DirectionsCarIcon fontSize="small" />,
	"Pricepy k legkovym avtomobilyam": <DirectionsCarIcon fontSize="small" />,
	"Gruzovye avtomobili, tyagachi": <LocalShippingIcon fontSize="small" />,
	"Pricepy i polupricepy k gruzovym avtomobilyam i tyagacham": <LocalShippingIcon fontSize="small" />,
	"Avtobusy": <DirectionsBusIcon fontSize="small" />,
	"Motocikly, motorrollery, motokoljaska i mopedy": <TwoWheelerIcon fontSize="small" />,
	"Selskokhozyajstvennaya i stroitelnaya tekhnika": <AgricultureIcon fontSize="small" />,
}

// Порядок сортировки (легковые первыми)
const vehicleTypeOrder = [
	"Legkovye avtomobili",
	"Gruzovye avtomobili, tyagachi",
	"Avtobusy",
	"Motocikly, motorrollery, motokoljaska i mopedy",
	"Pricepy k legkovym avtomobilyam",
	"Pricepy i polupricepy k gruzovym avtomobilyam i tyagacham",
	"Selskokhozyajstvennaya i stroitelnaya tekhnika",
]

const sortTariffs = (tariffs: Tariff[]): Tariff[] => {
	return [...tariffs].sort((a, b) => {
		const indexA = vehicleTypeOrder.indexOf(a.vehicleType)
		const indexB = vehicleTypeOrder.indexOf(b.vehicleType)
		return (indexA === -1 ? 999 : indexA) - (indexB === -1 ? 999 : indexB)
	})
}

type NavItem = {
	label: string
	path: string
	icon: React.ReactNode
	adminOnly?: boolean
}

const navItems: NavItem[] = [
	{ label: "Оформить синюю карту", path: "/policy", icon: <CreditCardIcon /> },
	{ label: "Оплаченные полиса", path: "/policies", icon: <CreditCardIcon /> },
	{ label: "Тарифы", path: "/tariffs", icon: <LocalOfferRoundedIcon /> },
	{ label: "Админ-панель", path: "/admin", icon: <AdminPanelSettingsIcon />, adminOnly: true },
]

export const Topbar: React.FC = () => {
	const theme = useTheme()
	const navigate = useNavigate()
	const location = useLocation()

	const { mode, toggleColorMode } = useColorMode()
	const { data: userData } = useAuthUser()
	const logout = useLogout()

	const isDark = theme.palette.mode === "dark"
	const iconBorder = isDark
		? "1px solid rgba(255, 255, 255, 0.15)"
		: `1px solid ${alpha(theme.palette.common.black, 0.12)}`
	const iconBg = isDark ? "rgba(255, 255, 255, 0.04)" : "rgba(15, 23, 42, 0.04)"
	const iconHoverBg = isDark
		? "rgba(255, 255, 255, 0.1)"
		: "rgba(15, 23, 42, 0.08)"

	const isAuthenticated = Boolean(userData)
	const userInitial = (userData?.name || userData?.email || "U")
		.slice(0, 1)
		.toUpperCase()

	const [drawerOpen, setDrawerOpen] = useState(false)
	const [profileAnchor, setProfileAnchor] = useState<null | HTMLElement>(null)
	
	// Состояния калькулятора
	const [calcAnchor, setCalcAnchor] = useState<null | HTMLElement>(null)
	const [tariffs, setTariffs] = useState<Tariff[]>([])
	const [calcLoading, setCalcLoading] = useState(false)
	const [selectedVehicle, setSelectedVehicle] = useState("")
	const [selectedPeriod, setSelectedPeriod] = useState("15days")

	const openProfileMenu = Boolean(profileAnchor)
	const openCalc = Boolean(calcAnchor)
	
	// Загрузка тарифов при открытии калькулятора
	useEffect(() => {
		if (openCalc && tariffs.length === 0) {
			setCalcLoading(true)
			TariffsApi.getAllTariffs()
				.then((data) => {
					setTariffs(data)
					if (data.length > 0) {
						// Выбираем первый элемент из отсортированного списка (легковые)
						const sorted = sortTariffs(data)
						setSelectedVehicle(sorted[0].vehicleType)
					}
				})
				.catch(() => {})
				.finally(() => setCalcLoading(false))
		}
	}, [openCalc, tariffs.length])
	
	// Расчет цены
	const getPrice = (): number | null => {
		if (!selectedVehicle || tariffs.length === 0) return null
		const tariff = tariffs.find(t => t.vehicleType === selectedVehicle)
		if (!tariff) return null
		
		if (selectedPeriod === "15days") return tariff.days15
		const monthKey = `month${selectedPeriod}` as keyof Tariff
		return tariff[monthKey] as number
	}
	
	const price = getPrice()

	const handleNavigate = (path: string) => {
		navigate(path)
		setDrawerOpen(false)
		setProfileAnchor(null)
	}

	const canSee = (item: NavItem) => {
		if (!item.adminOnly) return true
		return userData?.role === "ADMIN"
	}

	const handleLogout = async () => {
		try {
			await logout.mutateAsync()
			setProfileAnchor(null)
			navigate("/auth/login")
		} catch {
			setProfileAnchor(null)
		}
	}

	return (
		<>
			<AppBar
				position="fixed"
				elevation={0}
				sx={{
					background: isDark
						? "linear-gradient(90deg, #0f172a 0%, #111827 55%, #0b1324 100%)"
						: "linear-gradient(90deg, #ffffff 0%, #f8fafc 55%, #f1f5f9 100%)",
					color: isDark ? "#e5e7eb" : theme.palette.text.primary,
					backdropFilter: "blur(10px)",
					boxShadow: isDark
						? "0 12px 28px rgba(0, 0, 0, 0.35)"
						: "0 10px 24px rgba(15, 23, 42, 0.12)",
				}}
			>
				<Container
					maxWidth="lg"
					sx={{
						px: { xs: 2, sm: 3, md: 4 },
					}}
				>
					<Toolbar sx={{ minHeight: 56, gap: 2, px: 0 }}>
						<Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
							<IconButton
								onClick={() => setDrawerOpen(true)}
								aria-label="open navigation"
								sx={{
									display: { xs: "inline-flex", md: "none" },
									color: "inherit",
									border: iconBorder,
									borderRadius: 2,
									width: 36,
									height: 36,
									backgroundColor: iconBg,
									"&:hover": {
										backgroundColor: iconHoverBg,
									},
								}}
							>
								<MenuRoundedIcon fontSize="small" />
							</IconButton>

							<Box
								onClick={() => navigate("/")}
								sx={{
									fontWeight: 900,
									fontSize: 20,
									letterSpacing: 0.6,
									whiteSpace: "nowrap",
									cursor: "pointer",
									color: "#1f6feb",
									textShadow: `0 2px 10px ${alpha("#1f6feb", 0.4)}`,
								}}
							>
								Bluecards
							</Box>
						</Box>

						<Box
							sx={{
								flex: 1,
								display: { xs: "none", md: "flex" },
								justifyContent: "center",
								gap: 1,
							}}
						>
							{navItems.filter(canSee).map((item) => {
								const active = location.pathname === item.path
								return (
									<Button
										key={item.path}
										onClick={() => handleNavigate(item.path)}
										sx={{
											color: isDark
												? active
													? "#ffffff"
													: "rgba(255, 255, 255, 0.7)"
												: active
													? theme.palette.text.primary
													: theme.palette.text.secondary,
											fontWeight: 700,
											textTransform: "none",
											px: 1.5,
											py: 0.6,
											borderRadius: 999,
											backgroundColor: active
												? isDark
													? "rgba(31, 111, 235, 0.2)"
													: alpha(theme.palette.primary.main, 0.12)
												: "transparent",
											"&:hover": {
												backgroundColor: isDark
													? "rgba(31, 111, 235, 0.2)"
													: alpha(theme.palette.primary.main, 0.12),
												color: isDark
													? "#ffffff"
													: theme.palette.text.primary,
											},
										}}
									>
										{item.label}
									</Button>
								)
							})}
						</Box>

						<Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
							<Tooltip title="Калькулятор цен">
								<IconButton
									onClick={(e) => setCalcAnchor(e.currentTarget)}
									aria-label="open calculator"
									sx={{
										color: openCalc ? theme.palette.primary.main : "inherit",
										border: openCalc 
											? `1px solid ${theme.palette.primary.main}`
											: iconBorder,
										borderRadius: 2,
										width: 36,
										height: 36,
										backgroundColor: openCalc 
											? alpha(theme.palette.primary.main, 0.1)
											: iconBg,
										"&:hover": {
											backgroundColor: openCalc
												? alpha(theme.palette.primary.main, 0.15)
												: iconHoverBg,
										},
									}}
								>
									<CalculateRoundedIcon fontSize="small" />
								</IconButton>
							</Tooltip>

							<Tooltip title="Уведомления">
								<IconButton
									onClick={() => handleNavigate("/policies")}
									aria-label="open notifications"
									sx={{
										color: "inherit",
										border: iconBorder,
										borderRadius: 2,
										width: 36,
										height: 36,
										backgroundColor: iconBg,
										"&:hover": {
											backgroundColor: iconHoverBg,
										},
									}}
								>
									<NotificationsNoneRoundedIcon fontSize="small" />
								</IconButton>
							</Tooltip>

							<Tooltip title={mode === "dark" ? "Светлая тема" : "Тёмная тема"}>
								<IconButton
									onClick={toggleColorMode}
									aria-label="toggle theme"
									sx={{
										color: "inherit",
										border: iconBorder,
										borderRadius: 2,
										width: 36,
										height: 36,
										backgroundColor: iconBg,
										"&:hover": {
											backgroundColor: iconHoverBg,
										},
									}}
								>
									{mode === "dark" ? (
										<LightModeRoundedIcon fontSize="small" />
									) : (
										<DarkModeRoundedIcon fontSize="small" />
									)}
								</IconButton>
							</Tooltip>

							<Tooltip title={isAuthenticated ? "Профиль" : "Войти"}>
								<IconButton
									onClick={(e) => setProfileAnchor(e.currentTarget)}
									aria-label="open profile menu"
									sx={{
										border: iconBorder,
										borderRadius: 2,
										width: 36,
										height: 36,
										p: 0,
										backgroundColor: iconBg,
										color: "inherit",
										"&:hover": {
											backgroundColor: iconHoverBg,
										},
									}}
								>
									{isAuthenticated ? (
										<Avatar sx={{ width: 30, height: 30 }}>{userInitial}</Avatar>
									) : (
										<AccountCircleRoundedIcon fontSize="small" />
									)}
								</IconButton>
							</Tooltip>
						</Box>

						<Menu
							anchorEl={profileAnchor}
							open={openProfileMenu}
							onClose={() => setProfileAnchor(null)}
							anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
							transformOrigin={{ vertical: "top", horizontal: "right" }}
							PaperProps={{
								sx: {
									mt: 1,
									minWidth: 220,
									borderRadius: 2,
									border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
								},
							}}
						>
							{isAuthenticated ? (
								<>
									<MenuItem onClick={() => handleNavigate("/profile")}>
										<ListItemIcon>
											<PersonIcon fontSize="small" />
										</ListItemIcon>
										Данные профиля
									</MenuItem>
									<MenuItem onClick={() => handleNavigate("/policies")}>
										<ListItemIcon>
											<CreditCardIcon fontSize="small" />
										</ListItemIcon>
										Список заказов
									</MenuItem>
									<Divider />
									<MenuItem
										onClick={handleLogout}
										disabled={logout.isPending}
										sx={{ color: theme.palette.error.main }}
									>
										<ListItemIcon sx={{ color: "inherit" }}>
											<LogoutRoundedIcon fontSize="small" />
										</ListItemIcon>
										{logout.isPending ? "Выход..." : "Выйти"}
									</MenuItem>
								</>
							) : (
								<>
									<MenuItem onClick={() => handleNavigate("/auth/login")}>
										<ListItemIcon>
											<AccountCircleRoundedIcon fontSize="small" />
										</ListItemIcon>
										Войти
									</MenuItem>
									<MenuItem onClick={() => handleNavigate("/auth/register")}>
										<ListItemIcon>
											<PersonIcon fontSize="small" />
										</ListItemIcon>
										Регистрация
									</MenuItem>
								</>
							)}
						</Menu>
						
						{/* Popover калькулятора */}
						<Popover
							open={openCalc}
							anchorEl={calcAnchor}
							onClose={() => setCalcAnchor(null)}
							anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
							transformOrigin={{ vertical: "top", horizontal: "right" }}
							PaperProps={{
								sx: {
									mt: 1.5,
									p: 2.5,
									width: 300,
									borderRadius: 3,
									border: `1px solid ${alpha(theme.palette.primary.main, 0.25)}`,
									boxShadow: isDark
										? `0 8px 32px rgba(0, 0, 0, 0.5)`
										: `0 8px 32px ${alpha(theme.palette.common.black, 0.15)}`,
									background: isDark
										? "linear-gradient(145deg, #1a1f22 0%, #1e2428 100%)"
										: theme.palette.background.paper,
								},
							}}
						>
							<Typography
								variant="subtitle1"
								sx={{
									fontWeight: 700,
									mb: 2,
									display: "flex",
									alignItems: "center",
									gap: 1,
									color: theme.palette.primary.main,
								}}
							>
								<CalculateRoundedIcon fontSize="small" />
								Калькулятор цен
							</Typography>
							
							{calcLoading ? (
								<Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
									<CircularProgress size={32} />
								</Box>
							) : (
								<>
									<FormControl fullWidth size="small" sx={{ mb: 2 }}>
										<InputLabel>Тип транспорта</InputLabel>
										<Select
											value={selectedVehicle}
											label="Тип транспорта"
											onChange={(e) => setSelectedVehicle(e.target.value)}
											sx={{ borderRadius: 2 }}
										>
											{sortTariffs(tariffs).map((t) => (
												<MenuItem key={t.id} value={t.vehicleType}>
													<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
														{vehicleIcons[t.vehicleType]}
														<Typography variant="body2">
															{vehicleTypeTranslations[t.vehicleType] || t.vehicleType}
														</Typography>
													</Box>
												</MenuItem>
											))}
										</Select>
									</FormControl>
									
									<FormControl fullWidth size="small" sx={{ mb: 2.5 }}>
										<InputLabel>Срок страхования</InputLabel>
										<Select
											value={selectedPeriod}
											label="Срок страхования"
											onChange={(e) => setSelectedPeriod(e.target.value)}
											sx={{ borderRadius: 2 }}
										>
											{insurancePeriods.map((p) => (
												<MenuItem key={p.value} value={p.value}>
													{p.label}
												</MenuItem>
											))}
										</Select>
									</FormControl>
									
									{price !== null && (
										<Box
											sx={{
												p: 2,
												borderRadius: 2,
												background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)`,
												border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
												textAlign: "center",
											}}
										>
											<Typography variant="caption" color="text.secondary">
												Стоимость страховки
											</Typography>
											<Typography
												variant="h5"
												sx={{
													fontWeight: 800,
													color: theme.palette.primary.main,
													mt: 0.5,
												}}
											>
												{price.toLocaleString("ru-RU")} ₽
											</Typography>
										</Box>
									)}
									
									<Button
										fullWidth
										variant="contained"
										size="small"
										onClick={() => {
											setCalcAnchor(null)
											handleNavigate("/policy")
										}}
										sx={{
											mt: 2,
											borderRadius: 2,
											textTransform: "none",
											fontWeight: 600,
										}}
									>
										Оформить полис
									</Button>
								</>
							)}
						</Popover>
					</Toolbar>
				</Container>
			</AppBar>

			<Drawer
				open={drawerOpen}
				onClose={() => setDrawerOpen(false)}
				PaperProps={{
					sx: {
						width: 280,
						background: theme.palette.background.paper,
						borderRight: `1px solid ${theme.palette.divider}`,
					},
				}}
			>
				<Box sx={{ px: 2, py: 2, fontWeight: 900 }}>Навигация</Box>
				<List sx={{ pt: 0 }}>
					{navItems.filter(canSee).map((item) => {
						const active = location.pathname === item.path
						return (
							<ListItemButton
								key={item.path}
								onClick={() => handleNavigate(item.path)}
								sx={{
									mx: 1,
									mb: 0.5,
									borderRadius: 2,
									backgroundColor: active
										? alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.18 : 0.12)
										: "transparent",
								}}
							>
								<ListItemIcon sx={{ minWidth: 40, color: "text.secondary" }}>
									{item.icon}
								</ListItemIcon>
								<ListItemText
									primary={item.label}
									primaryTypographyProps={{ fontWeight: 700, fontSize: 14 }}
								/>
							</ListItemButton>
						)
					})}
				</List>
			</Drawer>
		</>
	)
}


